import { createContext, ReactNode, useContext, useMemo } from 'react';
import { Customer } from 'store/customers/customer';
import { ConfigurationStatus } from './utils';

type PartyContextValue = {
    customer: Customer;
    canUpdate: boolean;
    customerId: string;
    viewOnly: boolean;
    configurationStatus: ConfigurationStatus;
    setCustomer: (customer: Customer) => void;
    refreshCustomer: () => void;
};

type ProviderProps = {
    canUpdate: boolean;
    customer: Customer;
    customerId: string;
    viewOnly: boolean;
    configurationStatus: ConfigurationStatus;
    setCustomer: (customer: Customer) => void;
    refreshCustomer: () => void;
    children: ReactNode;
};

const PartyContext = createContext<PartyContextValue | undefined>(undefined);

export const usePartyContext = () => {
    const ctx = useContext(PartyContext);

    if (!ctx) {
        throw new Error('usePartyContext must be used within a PartyProvider');
    }

    return ctx;
};

export const PartyProvider = ({
    customer,
    canUpdate,
    customerId,
    children,
    setCustomer,
    refreshCustomer,
    viewOnly,
    configurationStatus,
}: ProviderProps) => {
    const value = useMemo(
        () => ({
            customer,
            canUpdate,
            customerId,
            setCustomer,
            refreshCustomer,
            viewOnly,
            configurationStatus,
        }),
        [customer, canUpdate, customerId, setCustomer, refreshCustomer, viewOnly, configurationStatus]
    );

    return <PartyContext.Provider value={value}>{children}</PartyContext.Provider>;
};
